package fr.manyverse.app.infrastructure.datastore

import fr.manyverse.app.domain.model.Sound
import fr.manyverse.app.domain.model.SoundTag
import fr.manyverse.app.domain.model.SoundType
import kotlinx.serialization.Serializable

/**
 * Interface for storing sound metadata locally.
 */
interface SoundMetadataDataStore {
    /**
     * Saves sound metadata.
     */
    suspend fun saveSoundMetadata(metadata: SoundMetadata): Result<Unit>

    /**
     * Gets sound metadata by ID.
     */
    suspend fun getSoundMetadata(soundId: String): SoundMetadata?

    /**
     * Gets all sounds for a user.
     */
    suspend fun getAllSoundsForUser(userId: String): List<SoundMetadata>

    /**
     * Gets sounds for a specific user and universe.
     */
    suspend fun getSoundsByUserAndUniverse(userId: String, universeId: String): List<SoundMetadata>

    /**
     * Deletes sound metadata.
     */
    suspend fun deleteSoundMetadata(soundId: String): Result<Unit>
}

/**
 * Serializable sound metadata for local storage.
 */
@Serializable
data class SoundMetadata(
    val id: String,
    val userId: String,
    val universeId: String,
    val name: String,
    val type: String,
    val tags: List<String>,
    val fileName: String,
    val localPath: String,
    val createdAt: Long = 0L,
    val durationSeconds: Int? = null
) {
    /**
     * Converts this metadata to a domain Sound model.
     */
    fun toDomain(): Sound = Sound(
        id = id,
        name = name,
        fileName = fileName,
        type = SoundType.valueOf(type),
        tags = tags.map { SoundTag.valueOf(it) }.toSet(),
        universeId = universeId,
        userId = userId,
        isUserUploaded = true,
        storagePath = localPath,
        durationSeconds = durationSeconds
    )

}
